import React, { useState, useRef, useEffect } from 'react';
import { FaTrophy, FaWhatsapp, FaDownload } from 'react-icons/fa';
import html2canvas from 'html2canvas';
import './ShareAchievementButton.css';

const ShareAchievementButton = ({ 
  name, 
  points, 
  solved, 
  requiredPoints, 
  requiredSolved,
  percentage,
  solvedPercentage,
  trackingMode,
  college,
  year,
  dept,
  cardRef,
  screenshotRef,
  lastFetched,
  isFirstLogin
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [menuPosition, setMenuPosition] = useState('bottom');
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  const toggleMenu = () => {
    setShowMenu(!showMenu);
  };

  const handleDownloadBadge = async () => {
    if (!screenshotRef || !screenshotRef.current) {
      alert('Unable to capture screenshot. Please try again.');
      return;
    }

    setIsGenerating(true);
    
    // Close menu first
    setShowMenu(false);
    
    // Wait for menu to close and UI to settle
    await new Promise(resolve => setTimeout(resolve, 100));
    
    try {
      // Capture the hidden screenshot div optimized for LinkedIn
      const canvas = await html2canvas(screenshotRef.current, {
        backgroundColor: '#1a1a1a',
        scale: 2,
        logging: false,
        useCORS: true,
        allowTaint: true
      });

      // Convert canvas to blob
      canvas.toBlob((blob) => {
        if (blob) {
          // Create download link
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          const timestamp = new Date().toISOString().slice(0, 10);
          link.href = url;
          link.download = `skillrack-achievement-${timestamp}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }
      }, 'image/png');
      
    } catch (error) {
      console.error('Badge generation failed:', error);
      alert('Failed to generate badge. Please try again later.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleShareProgressWhatsApp = () => {
    // const userName = name.split(' ')[0];
    const currentValue = trackingMode === 'points' ? points : solved;
    const requiredValue = trackingMode === 'points' ? requiredPoints : requiredSolved;
    const metric = trackingMode === 'points' ? 'points' : 'programs';
    
    let message = `My *SkillRack Progress* Update!\n\n`;
    message += `- *${
      (currentValue <= requiredValue && currentValue !== 0)
      ? `${currentValue}/${requiredValue}`
      : `${currentValue}`
    }* ${metric} completed\n\n`;
    
    if (currentValue >= requiredValue && requiredValue !== 0) {
      message += `- Target achieved!\n\n`;
    } else {
      message += `- Keep grinding!\n\n`;
    }
    
    message += `Track your progress too: https://skillrack.gururaja.in`;

    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
    setShowMenu(false);
  };

  const handleShareAppWhatsApp = () => {
    const message = `Check out *SkillRack Tracker*!\n\n` +
      `- Track your SkillRack points & programs\n` +
      `- _AI-powered_ schedule planning\n\n` +
      `Try it now: https://skillrack.gururaja.in`;

    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
    setShowMenu(false);
  };

  // Calculate available space and determine optimal menu position
  useEffect(() => {
    if (showMenu && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;
      
      // Need at least 200px for the menu to display comfortably
      // If not enough space below and more space above, show above
      if (spaceBelow < 200 && spaceAbove > spaceBelow) {
        setMenuPosition('top');
      } else {
        setMenuPosition('bottom');
      }
    }
  }, [showMenu]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showMenu && !event.target.closest('.share-achievement-container')) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      // Add a small delay before attaching the listener to prevent immediate closing
      const timer = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside, { passive: true });
      }, 100);

      return () => {
        clearTimeout(timer);
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('touchstart', handleClickOutside, { passive: true });
      };
    }
  }, [showMenu]);

  return (
    <div className="share-achievement-container">
      <button 
        ref={buttonRef}
        onClick={toggleMenu} 
        className="share-achievement-button"
        aria-label="Share Achievement"
      >
        <FaTrophy size={16} />
        <span>Share</span>
      </button>

      {showMenu && (
        <div 
          ref={menuRef}
          className={`share-menu fade-in ${menuPosition === 'top' ? 'position-top' : 'position-bottom'}`}
        >
          <button 
            onClick={handleShareProgressWhatsApp} 
            className="share-menu-item"
          >
            <FaWhatsapp size={18} />
            <div className="share-menu-text">
              <span className="share-menu-title">Share My Progress</span>
              <span className="share-menu-subtitle">WhatsApp with stats</span>
            </div>
          </button>

          <button 
            onClick={handleShareAppWhatsApp} 
            className="share-menu-item"
          >
            <FaWhatsapp size={18} />
            <div className="share-menu-text">
              <span className="share-menu-title">Share This App</span>
              <span className="share-menu-subtitle">Spread the word!</span>
            </div>
          </button>

          <button 
            onClick={handleDownloadBadge} 
            className="share-menu-item"
            disabled={isGenerating}
          >
            <FaDownload size={18} />
            <div className="share-menu-text">
              <span className="share-menu-title">
                {isGenerating ? 'Generating...' : 'Download Badge'}
              </span>
              <span className="share-menu-subtitle">For LinkedIn & Social Media</span>
            </div>
          </button>
        </div>
      )}
    </div>
  );
};

export default ShareAchievementButton;
